import { useEffect } from "react";
import { useImmer } from "use-immer";
import moment from "moment";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { attendanceCheckIn, GET_MY_PROGRAMS, useServerTime } from "modules";
import { errorFormatter } from "utils";
import { useAttendanceCheckQuery, useCountdown } from ".";

export const useAttendanceHandler = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const [enable, setEnable] = useImmer(false);
  const [newDate, setNewDate] = useImmer(null);
  const [attendanceProgram, setAttendanceProgram] = useImmer(
    localStorage.getItem("_stu_selectedProgram")
      ? JSON.parse(localStorage.getItem("_stu_selectedProgram"))
      : null
  );

  const localData = localStorage.getItem("_stu_selectedProgram")
    ? JSON.parse(localStorage.getItem("_stu_selectedProgram"))
    : null;

  const { data: serverTime, refetch: serverTimeRefetch } = useServerTime({
    load: true,
  });
  const { data: hasStudentMarkedAttendance, refetch } = useAttendanceCheckQuery(
    {
      studentBatchId: attendanceProgram
        ? attendanceProgram?.studentBatchId
        : localData?.studentBatchId,
      load: false,
    }
  );
  const validTill = serverTime
    ? moment(moment(serverTime).format("YYYY-MM-DD"))
        .add(1, "day")
        .format("YYYY-MM-DDTHH:mm:ss")
    : "";

  const { timer } = useCountdown({
    validTill,
    currentTime: serverTime,
  });

  useEffect(() => {
    let lastTime = Date.now();

    const onVisibilityChange = () => {
      if (!document.hidden) {
        serverTimeRefetch();
      }
    };

    const driftCheck = setInterval(() => {
      const now = Date.now();
      if (now - lastTime > 10000) {
        serverTimeRefetch();
        if (pathname === "/") {
          queryClient.invalidateQueries([GET_MY_PROGRAMS]);
        }
      }
      lastTime = now;
    }, 1000);

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(driftCheck);
    };
  }, [serverTimeRefetch]);

  useEffect(() => {
    if (
      attendanceProgram?.hasAttendance &&
      !attendanceProgram?.isAttendanceMarked &&
      serverTime
    ) {
      setEnable((draft) => {
        draft = true;
        return draft;
      });
    }
  }, [attendanceProgram]);

  useEffect(() => {
    if (!pathname.includes("program")) {
      setEnable((draft) => {
        draft = false;
        return draft;
      });
    }
  }, [pathname]);

  useEffect(() => {
    const data = attendanceProgram
      ? attendanceProgram
      : JSON.parse(localStorage.getItem("_stu_selectedProgram"));
    if (serverTime) {
      setNewDate((draft) => {
        draft = serverTime;
        return draft;
      });
      if (pathname !== "/" && data && data?.isAttendanceMarked !== null) {
        refetch();
      }
    }
  }, [serverTime]);

  useEffect(() => {
    const data = attendanceProgram
      ? attendanceProgram
      : JSON.parse(localStorage.getItem("_stu_selectedProgram"));
    if (
      data?.hasAttendance &&
      hasStudentMarkedAttendance !== undefined &&
      !hasStudentMarkedAttendance
    ) {
      setEnable((draft) => {
        draft = true;
        return draft;
      });
    }
  }, [hasStudentMarkedAttendance]);

  useEffect(() => {
    if (timer.hours === 0 && timer.minutes === 0 && timer.seconds === 0) {
      const data = attendanceProgram
        ? attendanceProgram
        : JSON.parse(localStorage.getItem("_stu_selectedProgram"));
      if (pathname === "/") {
        queryClient.invalidateQueries([GET_MY_PROGRAMS]);
      } else if (data && data?.hasAttendance) {
        setNewDate((draft) => {
          draft = moment(serverTime).add(1, "day").format();
          return draft;
        });
        setEnable((draft) => {
          draft = true;
          return draft;
        });
      }
    }
  }, [timer]);

  const onCloseModal = () => {
    setEnable((draft) => {
      draft = false;
      return draft;
    });
  };

  const attendanceMarking = useMutation({
    mutationFn: attendanceCheckIn,
    onSuccess: async () => {
      if (pathname === "/") {
        localStorage.setItem(
          "_stu_selectedProgram",
          JSON.stringify({ ...attendanceProgram, isAttendanceMarked: true })
        );
        navigate("/program");
      }
    },
    onError: (e) => {
      errorFormatter(e);
    },
    onSettled: () => {
      onCloseModal();
    },
  });

  const onCheckIn = () => {
    const data = attendanceProgram
      ? attendanceProgram
      : JSON.parse(localStorage.getItem("_stu_selectedProgram"));
    attendanceMarking.mutate({
      studentBatchId: data?.studentBatchId,
    });
  };

  const onClickProgram = (attendanceProgram) => {
    setAttendanceProgram((draft) => {
      draft = attendanceProgram;
      return draft;
    });
  };

  return {
    onCheckIn,
    onClickProgram,
    enable,
    serverTime,
    onCloseModal,
    newDate,
    attendanceMarking,
  };
};
